import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, Grid,
  Typography, Alert, Checkbox, FormControlLabel, RadioGroup, Radio, FormControl, FormLabel,
  Divider, Chip, Box, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, MenuItem
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { useCurrency } from '../../context/CurrencyContext';

// Moving a tenant to another unit, as one guided step.
//
// Everything the server would do is shown BEFORE the button is enabled: what the tenant
// owes, what happens to their deposit, which invoice is cancelled and what is raised in its
// place. The preview is the server's own assessment (GET /tenant-transfers/preview), so what
// the person reads is exactly what POST will do — there is no second copy of the rules here.

const today = () => new Date().toISOString().split('T')[0];

const REASONS = [
  ['upgrade', 'Upgrade to a bigger or better unit'],
  ['downsize', 'Downsize'],
  ['landlord_request', 'Landlord request'],
  ['maintenance', 'Unit needs repair or maintenance'],
  ['dispute', 'Dispute'],
  ['other', 'Other']
];

const ENDED_BY = {
  transfer: 'Transferred out',
  exit: 'Exited',
  reassignment: 'Reassigned',
  deletion: 'Deleted',
  landlord_deactivated: 'Landlord deactivated'
};

const initialForm = (tenant) => ({
  propertyId: '',
  unitId: '',
  transferDate: today(),
  leaseEndDate: '',
  paymentDate: tenant?.paymentDate || 1,
  billingCycleDay: tenant?.billingCycleDay || 1,
  reason: 'other',
  notes: '',
  // Blank means "the server's default", which is to pro-rate when it can.
  rentHandling: '',
  carryArrears: false,
  acknowledgeAdvances: false,
  expectDeposit: false
});

const TenantTransferDialog = ({ open, tenant, onClose, onDone }) => {
  const { formatMoney } = useCurrency();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(initialForm(tenant));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const tenantId = tenant?._id || tenant?.id;

  useEffect(() => {
    if (!open || !tenantId) return;
    setForm(initialForm(tenant));
    setPreview(null);
    setResult(null);
    setUnits([]);

    axios.get('/properties?availableForAssignment=true')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setProperties(list.filter((p) => p.propertyType !== 'commercial' && p.landlord && p.landlord.isActive !== false));
      })
      .catch(() => toast.error('Could not load properties'));

    axios.get(`/tenants/${tenantId}/tenancies`)
      .then((res) => setHistory(res.data || []))
      .catch(() => setHistory([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  useEffect(() => {
    if (!form.propertyId) { setUnits([]); return; }
    axios.get(`/units/property/${form.propertyId}`)
      .then((res) => setUnits((res.data || []).filter((u) => u.status === 'available')))
      .catch(() => toast.error('Could not load units'));
  }, [form.propertyId]);

  // The preview is the server's assessment, refreshed as the form changes. Debounced so a
  // typed date does not fire a request per keystroke.
  useEffect(() => {
    if (!open || !tenantId || !form.unitId || !form.transferDate) { setPreview(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await axios.get('/tenant-transfers/preview', {
          params: {
            tenantId,
            toUnitId: form.unitId,
            transferDate: form.transferDate,
            billingCycleDay: form.billingCycleDay,
            paymentDate: form.paymentDate,
            leaseEndDate: form.leaseEndDate || undefined,
            rentHandling: form.rentHandling || undefined,
            carryArrears: form.carryArrears,
            acknowledgeAdvances: form.acknowledgeAdvances,
            expectDeposit: form.expectDeposit
          }
        });
        if (!cancelled) setPreview(res.data);
      } catch (error) {
        if (!cancelled) {
          setPreview(null);
          toast.error(error.response?.data?.message || 'Could not preview the transfer');
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [
    open, tenantId, form.unitId, form.transferDate, form.billingCycleDay, form.paymentDate,
    form.leaseEndDate, form.rentHandling, form.carryArrears, form.acknowledgeAdvances, form.expectDeposit
  ]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!preview?.ok) return;
    setSubmitting(true);
    try {
      const res = await axios.post('/tenant-transfers', {
        tenantId,
        toUnitId: form.unitId,
        transferDate: form.transferDate,
        leaseEndDate: form.leaseEndDate || null,
        paymentDate: form.paymentDate,
        billingCycleDay: form.billingCycleDay,
        reason: form.reason,
        notes: form.notes,
        rentHandling: form.rentHandling || undefined,
        carryArrears: form.carryArrears,
        acknowledgeAdvances: form.acknowledgeAdvances,
        expectDeposit: form.expectDeposit
      });
      setResult(res.data);
      toast.success(`${tenant.name} transferred`);
      if (onDone) onDone(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not transfer the tenant');
      if (error.response?.data?.assessment) setPreview(error.response.data.assessment);
    } finally {
      setSubmitting(false);
    }
  };

  const rent = preview?.rent;
  const account = preview?.account;
  const prorateOption = rent?.options?.prorate;
  const effectiveHandling = form.rentHandling || rent?.handling || '';

  const renderPart = (label, part) => part && (
    <Typography variant="body2">
      {label}: {part.location}, {part.periodStart} to {part.periodEnd} at {formatMoney(part.monthlyRent)} a month
      {' '}= <strong>{formatMoney(part.amount)}</strong>
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Transfer {tenant?.name || 'tenant'}</DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            <Alert severity={result.issues?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {tenant?.name} is now in {result.toProperty?.propertyName} - Unit {result.toUnit?.unitNumber}
              {' '}from {result.transferDate}.
            </Alert>
            {result.cancelledInvoice && (
              <Typography variant="body2">Cancelled {result.cancelledInvoice.invoiceNumber}.</Typography>
            )}
            {result.closingInvoice && (
              <Typography variant="body2">
                Raised {result.closingInvoice.invoiceNumber} for the old unit: {formatMoney(result.closingInvoice.amount)}.
              </Typography>
            )}
            {result.openingInvoice && (
              <Typography variant="body2">
                Raised {result.openingInvoice.invoiceNumber} for the new unit: {formatMoney(result.openingInvoice.amount)}.
              </Typography>
            )}
            {!!result.issues?.length && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Needs attention</Typography>
                {result.issues.map((i, idx) => <Typography key={idx} variant="body2">{i}</Typography>)}
              </Alert>
            )}
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Now in {tenant?.unit?.property?.propertyName || tenant?.property?.propertyName || '—'}
                {tenant?.unit?.unitNumber ? ` - Unit ${tenant.unit.unitNumber}` : ''}.
                The old tenancy ends the day before the transfer date; the new one starts on it.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={properties}
                value={properties.find((p) => p._id === form.propertyId) || null}
                getOptionLabel={(p) => `${p.propertyName}${p.addressStreet ? ` - ${p.addressStreet}` : ''}`}
                onChange={(_, val) => set({ propertyId: val?._id || '', unitId: '' })}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => <TextField {...params} label="To property" required />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={units}
                value={units.find((u) => u._id === form.unitId) || null}
                getOptionLabel={(u) => `${u.unitNumber} - ${formatMoney(u.monthlyRent, { decimals: 0 })} a month`}
                onChange={(_, val) => set({ unitId: val?._id || '' })}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                disabled={!form.propertyId}
                noOptionsText={form.propertyId ? 'No available units' : 'Choose a property first'}
                renderInput={(params) => <TextField {...params} label="To unit (available only)" required />}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Transfer date"
                type="date"
                value={form.transferDate}
                onChange={(e) => set({ transferDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="First day in the new unit"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Lease end (optional)"
                type="date"
                value={form.leaseEndDate}
                onChange={(e) => set({ leaseEndDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Pay day"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={form.paymentDate}
                onChange={(e) => set({ paymentDate: parseInt(e.target.value, 10) || 1 })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Cycle day"
                type="number"
                inputProps={{ min: 1, max: 31 }}
                value={form.billingCycleDay}
                onChange={(e) => set({ billingCycleDay: parseInt(e.target.value, 10) || 1 })}
                helperText="Day the rent month starts"
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField select fullWidth label="Reason" value={form.reason} onChange={(e) => set({ reason: e.target.value })}>
                {REASONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField fullWidth label="Notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* THE PREVIEW */}
            <Grid item xs={12}>
              {!form.unitId && (
                <Typography variant="body2" color="text.secondary">Choose the destination unit to see what the transfer will do.</Typography>
              )}
              {form.unitId && previewing && !preview && <CircularProgress size={20} />}
              {preview && (
                <Box sx={{ opacity: previewing ? 0.6 : 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {preview.fromLocation} ({formatMoney(preview.from?.monthlyRent)} a month)
                    {' → '}
                    {preview.toLocation} ({formatMoney(preview.to?.monthlyRent)} a month)
                    {preview.sameLandlord
                      ? <Chip size="small" label="same landlord" sx={{ ml: 1 }} />
                      : <Chip size="small" color="warning" label={`${preview.from?.landlordName || 'landlord'} → ${preview.to?.landlordName || 'landlord'}`} sx={{ ml: 1 }} />}
                  </Typography>

                  {!!preview.blockers?.length && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {preview.blockers.map((b, i) => <Typography key={i} variant="body2">{b}</Typography>)}
                    </Alert>
                  )}
                  {!!preview.warnings?.length && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {preview.warnings.map((w, i) => <Typography key={i} variant="body2">{w}</Typography>)}
                    </Alert>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Final account</Typography>
                  <Typography variant="body2">
                    Open invoices: {formatMoney(account?.invoiceBalance || 0)}
                    {account?.openInvoices?.length ? ` on ${account.openInvoices.length} invoice(s)` : ''}
                  </Typography>
                  <Typography variant="body2">Deposit held: {formatMoney(account?.depositHeld || 0)}</Typography>
                  <Typography variant="body2">Landlord advances outstanding (net): {formatMoney(account?.advanceNet || 0)}</Typography>

                  {(account?.invoiceBalance || 0) > 0 && (
                    <FormControlLabel
                      control={<Checkbox checked={form.carryArrears} onChange={(e) => set({ carryArrears: e.target.checked })} />}
                      label="Carry arrears forward. The invoices keep their property and still pay that landlord when collected."
                    />
                  )}
                  {(account?.advanceNet || 0) > 0 && !preview.sameLandlord && (
                    <FormControlLabel
                      control={<Checkbox checked={form.acknowledgeAdvances} onChange={(e) => set({ acknowledgeAdvances: e.target.checked })} />}
                      label="I have checked the outstanding advances to the previous landlord."
                    />
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Security deposit</Typography>
                  <Typography variant="body2">{preview.deposit?.text}</Typography>
                  {(preview.deposit?.action === 'none' || preview.deposit?.action === 'expected') && (
                    <FormControlLabel
                      control={<Checkbox checked={form.expectDeposit} onChange={(e) => set({ expectDeposit: e.target.checked })} />}
                      label="Expect a deposit of one month's rent at the new unit"
                    />
                  )}

                  <FormControl sx={{ mt: 1 }}>
                    <FormLabel>Rent for the month of the move</FormLabel>
                    <RadioGroup value={effectiveHandling} onChange={(e) => set({ rentHandling: e.target.value })}>
                      <FormControlLabel
                        value="prorate"
                        control={<Radio />}
                        disabled={!prorateOption?.available}
                        label={rent?.currentInvoice
                          ? `Pro-rate: cancel ${rent.currentInvoice.invoiceNumber} and bill each unit for its own days`
                          : 'Pro-rate: bill the new unit from the transfer date to the end of the rent month'}
                      />
                      {!prorateOption?.available && prorateOption?.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1 }}>{prorateOption.reason}</Typography>
                      )}
                      <FormControlLabel
                        value="next_cycle"
                        control={<Radio />}
                        label={rent?.currentInvoice
                          ? `Leave ${rent.currentInvoice.invoiceNumber} as it is; bill the new unit from the next rent month`
                          : 'Raise nothing now; the nightly run bills the new unit'}
                      />
                    </RadioGroup>
                  </FormControl>
                  {effectiveHandling === 'prorate' && (
                    <Box sx={{ ml: 1 }}>
                      {rent?.currentInvoice && (
                        <Typography variant="body2">
                          Cancel {rent.currentInvoice.invoiceNumber}: {rent.currentInvoice.periodStart} to {rent.currentInvoice.periodEnd},
                          {' '}{formatMoney(rent.currentInvoice.amount)}
                        </Typography>
                      )}
                      {renderPart('Raise for the old unit', rent?.closing)}
                      {renderPart('Raise for the new unit', rent?.opening)}
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            {!!history.length && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2">Tenancy history</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Where</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Rent</TableCell>
                      <TableCell>Ended by</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>
                          {h.property?.propertyName || '—'}{h.unit?.unitNumber ? ` - Unit ${h.unit.unitNumber}` : ''}
                        </TableCell>
                        <TableCell>{h.startDate}</TableCell>
                        <TableCell>{h.endDate || <Chip size="small" color="success" label="current" />}</TableCell>
                        <TableCell>{h.monthlyRent ? formatMoney(h.monthlyRent) : '—'}</TableCell>
                        <TableCell>{h.endedBy ? ENDED_BY[h.endedBy] || h.endedBy : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={!preview?.ok || previewing || submitting}
          >
            {submitting ? 'Transferring…' : 'Transfer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TenantTransferDialog;
